import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Button } from './ui/button';
import { Sheet, SheetContent, SheetTrigger } from './ui/sheet';
import { Menu, Phone, Mail, LockKeyhole } from 'lucide-react';

const Header = () => {
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);

  const navigation = [
    { name: 'Home', href: '/' },
    { name: 'About', href: '/about' },
    { name: 'Program', href: '/program' },
    { name: 'Health & Safety', href: '/health-safety' },
    { name: 'Photos', href: '/photos' },
    { name: 'Parents', href: '/parent-partnership' },
    { name: 'Contact', href: '/contact' },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <header className="sticky top-0 z-50 bg-white shadow-sm border-b-2 border-pink-200">
      {/* Top Bar with Contact Info */}
      <div className="bg-gradient-to-r from-purple-500 to-pink-500 text-white py-2">
        <div className="container mx-auto px-4">
          <div className="flex justify-between items-center text-xs md:text-sm">
            <div className="flex items-center space-x-3 md:space-x-4">
              <a 
                href="mailto:info@mkids.ca"
                className="flex items-center space-x-1 hover:text-yellow-200 transition-colors"
              >
                <Mail className="h-3 w-3 text-green-200" />
                <span>info@mkids.ca</span>
              </a>
              <div className="hidden sm:flex items-center space-x-1 text-yellow-200">
                <Phone className="h-3 w-3" />
                <span className="hidden md:inline">Mon-Fri: 7AM-5PM</span>
                <span className="sm:inline md:hidden">7AM-5PM</span>
              </div>
            </div>
            <div className="flex items-center space-x-2 md:space-x-4">
              <a 
                href="https://instagram.com/maplekidsacademy" 
                target="_blank" 
                rel="noopener noreferrer"
                className="flex items-center space-x-1 md:space-x-2 bg-white/20 hover:bg-white/30 px-2 md:px-3 py-1 rounded-full transition-all duration-200"
              >
                <svg className="h-3 w-3 md:h-4 md:w-4 text-pink-200" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.90-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                </svg>
                <span className="text-xs md:text-sm">@maplekidsacademy</span>
              </a>
              <div className="hidden lg:block bg-white/20 px-3 py-1 rounded-full">
                <span className="text-xs">Ages 0-36 Months • Maple Ridge, BC</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Super Clean & Small Navigation */}
      <nav className="container mx-auto py-2">
        <div className="flex items-center justify-between">
          {/* Compact Logo */}
          <Link to="/" className="flex items-center space-x-3">
            <img 
              src="/images/logo.png" 
              alt="Maple Kids Academy" 
              className="h-14 w-14 md:h-16 md:w-16 rounded-full shadow-lg border-2 border-purple-300 hover:border-pink-300 transition-colors"
            />
            <div>
              <h1 className="text-xl md:text-2xl font-bold text-purple-600">Maple Kids Academy</h1>
              <p className="text-sm md:text-base text-pink-500 -mt-1">Licensed Childcare • Ages 0-36m</p>
            </div>
          </Link>

          {/* Desktop Navigation - Super Organized */}
          <div className="hidden lg:flex items-center bg-gradient-to-r from-pink-50 to-purple-50 px-3 py-1 rounded-full border border-pink-200">
            {navigation.map((item) => (
              <Link
                key={item.name}
                to={item.href}
                className={`px-3 py-1 rounded-full text-sm font-medium transition-all duration-200 ${
                  isActive(item.href) 
                    ? 'bg-purple-500 text-white shadow-sm' 
                    : 'text-purple-600 hover:bg-pink-100 hover:text-purple-700'
                }`}
              >
                {item.name}
              </Link>
            ))}
          </div>

          {/* Compact CTA Buttons */}
          <div className="hidden md:flex items-center space-x-2">
            <Button 
              asChild 
              size="sm"
              className="bg-yellow-400 hover:bg-yellow-500 text-purple-800 font-medium px-4 py-2 rounded-full shadow-sm hover:shadow-md transition-all"
            >
              <Link to="/contact">Book Tour</Link>
            </Button>
            <Button 
              asChild 
              size="sm"
              className="bg-purple-500 hover:bg-purple-600 text-white font-medium px-4 py-2 rounded-full shadow-sm hover:shadow-md transition-all"
            >
              <Link to="/enroll"><LockKeyhole className="h-3 w-3 mr-1" /> Join Us</Link>
            </Button>
          </div>

          {/* Mobile menu */}
          <Sheet open={isOpen} onOpenChange={setIsOpen}>
            <SheetTrigger asChild className="lg:hidden">
              <Button variant="ghost" size="sm" className="p-2 text-purple-600">
                <Menu className="h-5 w-5" />
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-72 bg-gradient-to-b from-pink-50 to-purple-50">
              <div className="flex flex-col h-full">
                <div className="p-4 border-b border-pink-200">
                  <h3 className="text-lg font-bold text-purple-600">Menu</h3>
                </div>

                <div className="flex-1 py-4 space-y-1">
                  {navigation.map((item) => (
                    <Link
                      key={item.name}
                      to={item.href}
                      onClick={() => setIsOpen(false)}
                      className={`block mx-2 px-4 py-2 rounded-lg font-medium transition-all ${
                        isActive(item.href) 
                          ? 'bg-purple-500 text-white shadow-sm' 
                          : 'text-purple-600 hover:bg-pink-100'
                      }`}
                    >
                      {item.name}
                    </Link>
                  ))}
                </div>

                <div className="p-4 border-t border-pink-200 space-y-2">
                  <Button 
                    asChild 
                    className="w-full bg-yellow-400 hover:bg-yellow-500 text-purple-800 rounded-full"
                    onClick={() => setIsOpen(false)}
                  >
                    <Link to="/contact">Book Tour</Link>
                  </Button>
                  <Button 
                    asChild 
                    className="w-full bg-purple-500 hover:bg-purple-600 text-white rounded-full"
                    onClick={() => setIsOpen(false)}
                  >
                    <Link to="/enroll"><LockKeyhole className="h-3 w-3 mr-1" /> Join Us</Link>
                  </Button>
                </div>

                <div className="p-4 bg-white/50 rounded-t-lg">
                  <div className="space-y-1 text-sm text-purple-600">
                    <div className="flex items-center space-x-2">
                      <Mail className="h-4 w-4" />
                      <span>info@mkids.ca</span>
                    </div>
                    <p>Maple Ridge, BC</p>
                  </div>
                </div>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </nav>
    </header>
  );
};

export default Header;